/**
 * First-visit Welcome modal for the Wind-Drift Visualizer.
 *
 * The Forge is dense — a cold tester landing on Wind-Drift sees a 3D
 * scene, 40+ input controls, two score panels, and a Forge Score
 * Optimizer. Without context, that's a bounce risk.
 *
 * This modal surfaces on first visit (localStorage flag), explains what
 * the tool is and isn't in 4 sentences, and nudges the user to load a
 * starter scenario from the strip below.
 *
 * Dismiss state is per-browser, NOT per-user — sign-in isn't required to
 * see or hide it. Re-show via dev console: localStorage.removeItem('sparrow_wind_drift_welcome_v1')
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'sparrow_wind_drift_welcome_v1';

interface WelcomeModalProps {
  /** Optional override — if true, force-open. Useful for "How does this work?" button. */
  forceOpen?: boolean;
  onClose?: () => void;
}

export function WelcomeModal({ forceOpen, onClose }: WelcomeModalProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      return;
    }
    /* Only show on truly-first-visit. We want to avoid showing it to
     * Sal during dev — he's already seen it more times than the rest of
     * humanity combined. */
    if (typeof window === 'undefined') return;
    try {
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen) setOpen(true);
    } catch {
      /* localStorage blocked — just don't show */
    }
  }, [forceOpen]);

  const dismiss = (rememberFlag = true) => {
    setOpen(false);
    if (rememberFlag) {
      try {
        window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
      } catch { /* */ }
    }
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="welcome-overlay" onClick={() => dismiss(true)} role="dialog" aria-modal="true">
      <div className="welcome-card" onClick={(e) => e.stopPropagation()}>
        <p className="welcome-stamp">Sparrow Forge · 30-second intro</p>
        <h2 className="welcome-title">See what your arrow is doing.</h2>

        <p className="welcome-lede">
          Two builds side by side. Your wind, altitude, range. A 3D scene that shows
          drop, drift, and where the apex is — calibrated against documented hunts.
        </p>

        <ul className="welcome-bullets">
          <li>
            <strong>Compare two builds</strong> by tweaking grain weight, FOC, vane geometry, or
            tune quality. Forge Score updates live.
          </li>
          <li>
            <strong>Run a scenario</strong> from the strip below — eight pre-stocked comparisons
            (western elk, plains game, target, altitude effects).
          </li>
          <li>
            <strong>Measure your tune</strong> with the &quot;Measure tune&quot; link in either score panel —
            bareshaft offset or group-photo upload converts to impact yaw.
          </li>
        </ul>

        <p className="welcome-note">
          This isn't a chronograph replacement and it isn't an Easton spine chart.
          It's a calibrated drift-and-tune visualizer for hunters who want to know
          why their arrow does what it does.
        </p>

        <div className="welcome-actions">
          <button
            type="button"
            className="welcome-primary"
            onClick={() => dismiss(true)}
          >
            Got it — show me the scene
          </button>
          <button
            type="button"
            className="welcome-secondary"
            onClick={() => dismiss(false)}
          >
            Close (show again next time)
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Standalone hook for triggering the modal from a header "?" / "How does this work?" button.
 * Returns [open, openModal, closeModal].
 */
export function useWelcomeModal(): [boolean, () => void, () => void] {
  const [open, setOpen] = useState(false);
  return [open, () => setOpen(true), () => setOpen(false)];
}
